import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as DeleteSVG } from "App/common/icons/trash.svg";
import { DEFAULT_COLUMNS, EntityTable } from "App/components/EntityTable";
import { ENTITY_TYPE } from "App/components/EntityTable/config";
import { highnote } from "@highnote/server/src/sdk";
import { v4 as uuidv4 } from "uuid";
import { FileEntity } from "@highnote/server/src/core/entities";
import { useConfirmation } from "App/common/useConfirmation";
import { useToast } from "App/common/useToast";
import { useShareDialog } from "App/components/ShareDialog";
import { FileReferences } from "./FileReferences";
import { FileListSkeleton } from "./FileListSkeleton";

const defaultColumns = [DEFAULT_COLUMNS.PREVIEW, DEFAULT_COLUMNS.ACTIONS];

export const ManageTrackVersions = () => {
  const { renderShareDialog } = useShareDialog();
  const [renderTrigger, setRenderTrigger] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [files, setFiles] = useState<FileEntity[]>([]);

  const { toasted } = useToast();
  const { confirm, renderConfirmation } = useConfirmation();

  useEffect(() => {
    let unmounted: boolean;
    highnote.getStorageFiles({ type: "trackVersions" }).then((files) => {
      if (unmounted) return;
      setFiles(files);
      setLoading(false);
    });

    return () => {
      unmounted = true;
    };
  }, [renderTrigger]);

  const deleteTrackVersion = async (file: FileEntity) => {
    const references = await highnote.getFileReferences({
      id: file.id,
    });

    await confirm({
      title: `Delete ${file.fileName}?`,
      body: (
        <p>
          <FileReferences file={file} references={references} />
          <br />
          <br />
          <strong>
            Are you sure you want to permanently delete this file?
          </strong>{" "}
          Deleting this file may cause related items to break.
        </p>
      ),
    });

    const promises = [
      highnote.deleteStorageFile({
        storagePath: file.storagePath,
      }),
    ] as Promise<unknown>[];

    toasted({
      promise: Promise.all(promises).then(() => {
        setRenderTrigger(uuidv4());
      }),
      createMessage: `Deleting ${file.fileName}...`,
      successMessage: `${file.fileName} permanently deleted.`,
      errorMessage: `Could not delete ${file.fileName}. Please try again.`,
    });
  };

  const rows = useMemo(() => {
    return files
      .sort((a, b) => b.size - a.size)
      .map((entity) => {
        const fileName = entity.fileName || "Untitled";
        const cleanEntity = { ...entity, fileName };
        return {
          id: entity.id,
          entity: cleanEntity,
          type: ENTITY_TYPE.FILE,
          actions: [
            {
              name: "Delete File",
              icon: <DeleteSVG />,
              warn: true,
              onClick: async () => await deleteTrackVersion(cleanEntity),
            },
          ],
        };
      });
  }, [deleteTrackVersion]);

  if (loading) {
    return <FileListSkeleton />;
  }

  return (
    <>
      {renderShareDialog}
      {renderConfirmation}
      {rows.length > 0 ? (
        <EntityTable rows={rows} columns={defaultColumns} />
      ) : (
        <p>No tracks found.</p>
      )}
    </>
  );
};
